import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/Slice/authSlice";
import { errorMessage } from "../services";
import { clearCatalog } from "../services/admin";
import { track } from "../utils/activityTracker";
import { getMedia, MAX_IMAGE_BYTES, uploadMedia, IMAGE_LIMITS } from "../services/media";
import { bytes } from "../utils/format";
import { toast } from "../utils/toast";
import { FEATURED_LIMIT, OFFERS_LIMIT, STORE_TYPES, deleteProductNow, loadSite, mergeSavedProduct, newestProducts, saveProductNow, saveSite } from "./editor/siteData";
import "./admin.css";
import { can } from "./admin/permissions";
import { IMAGE_SLOTS, MAX_PHOTO_BYTES, prepareUpload } from "../utils/imageFit";
import CatalogTransfer from "./editor/CatalogTransfer";
import ComboBuilder from "./editor/ComboBuilder";
import SeoFields from "./editor/SeoFields";
import PriceFields from "./editor/PriceFields";
import ShopByEditor from "./editor/ShopByEditor";
import VariantFields, { GENERAL_PRESETS, GIFT_PRESETS, PersonalizationFields, SELFCARE_PRESETS, SIZE_PRESETS } from "./editor/VariantFields";
import Loader, { Spinner } from "../components/Loader";
import StoreSettingsForm from "./admin/StoreSettingsForm";
import BlogManager from "./admin/BlogManager";
import { storeBlog } from "../services/blog";

// Tabs of the editor, grouped by what they change. ?tab=Products (etc.) in the address opens that tab, so guides can
// link straight to it. Categories come before Products: a product is always added to a category
const TAB_GROUPS = [
  { label: "Home page", tabs: ["Content", "Featured", "Offers"] },
  { label: "Catalogue", tabs: ["Categories", "Products"] },
  { label: "Pages", tabs: ["About us", "Blog", "Policies"] },
  { label: "Look & settings", tabs: ["Design", "SEO", "Shipping & tax", "Store settings"] },
];
const EDITOR_TABS = TAB_GROUPS.flatMap((g) => g.tabs);
// Icon and one line on what each tab changes, shown on the tab and at the top of its section
const TAB_INFO = {
  Content: ["🏠", "Announcement bar, banners and the small cards on your home page."],
  Featured: ["⭐", "The products shown near the top of your home page."],
  Offers: ["🏷️", "Discounted products and combo packs, in their own row and menu link."],
  Categories: ["🗂️", "The groups your products are listed under. Switch one off to hide it."],
  Products: ["📦", "Add and edit products: name, price, stock, photo and category."],
  "About us": ["👋", "Your store's story, on its own About us page."],
  Blog: ["📝", "Posts for your store's Blog page."],
  Policies: ["📄", "Privacy policy and terms & conditions."],
  Design: ["🎨", "Store name, type, logo and theme colour."],
  SEO: ["🔍", "How your store shows up on Google and in shared links."],
  "Shipping & tax": ["🚚", "Delivery fee, free-shipping amount and GST (gift stores: gift wrap, card and delivery date)."],
  "Store settings": ["⚙️", "Business category, domain and image storage."],
};
// Shipping and GST are store settings, which a Super Admin and the store owner manage (not a franchise partner)
const SETTINGS_ROLES = ["superadmin", "storeadmin"];
// Gift options show only for gift stores (StoreSettingsForm filters cards by store type)
const SHIPPING_CARD = ["store-shipping", "store-gifting"];
const THEME_COLORS = ["#31862f", "#2456a6", "#8b4d9e", "#d06b32", "#172b4d", "#c34f72"];
const EMPTY_PRODUCT = { name: "", desc: "", cat: "", price: 0, originalPrice: 0, stock: 0, unitNote: "per pc", availability: "In stock", img: "", tags: [] };
const parseTags = (text) => text.split(",").map((t) => t.trim()).filter(Boolean);

function Field({ label, value, onChange, placeholder, disabled }) {
  return <label className="admin-field">{label}<input value={value ?? ""} placeholder={placeholder || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} /></label>;
}

// Products: up to 3 photos as square tiles, the first one the main photo. The empty tile opens the photo picker
// (several photos at once); a photo can also come from a link.
const GALLERY_SLOTS = 2;
function ProductPhotos({ img = "", onImgChange, gallery = [], onGalleryChange, siteId, notify, onUploaded, hasGallery }) {
  const max = hasGallery ? 1 + GALLERY_SLOTS : 1;
  const photos = [img, ...(hasGallery ? gallery : [])].filter(Boolean).slice(0, max);
  const { busy, upload } = useImageUpload(siteId, notify, "product");
  const [link, setLink] = useState("");
  const [showLink, setShowLink] = useState(false);
  const setPhotos = (list) => {
    onImgChange(list[0] || "");
    if (hasGallery) onGalleryChange(list.slice(1, max));
  };
  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    let list = [...photos];
    for (const file of files.slice(0, max - list.length)) {
      const url = await upload(file);
      if (url) list = [...list, url];
    }
    if (list.length !== photos.length) { setPhotos(list); onUploaded(); }
  };
  const addLink = () => {
    if (!link.trim()) return;
    setPhotos([...photos, link.trim()].slice(0, max));
    setLink("");
    setShowLink(false);
  };
  return (
    <div className="photo-tiles-wrap">
      <div className="photo-tiles">
        {photos.map((url, i) => (
          <div className="photo-tile" key={`${url}${i}`}>
            <img src={url} alt={`Photo ${i + 1}`} />
            {i === 0 ? <em className="photo-tile-badge">Main</em> : <button type="button" className="photo-tile-badge as-btn" onClick={() => setPhotos([url, ...photos.filter((_, j) => j !== i)])}>Make main</button>}
            <button type="button" className="photo-tile-remove" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} aria-label={`Remove photo ${i + 1}`}>×</button>
          </div>
        ))}
        {photos.length < max && (
          <label className={`photo-tile add ${busy ? "busy" : ""}`}>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple={hasGallery} onChange={pick} disabled={busy} />
            <span aria-hidden="true">{busy ? "⏳" : "📷"}</span>
            <b>{busy ? "Uploading…" : photos.length ? "Add photo" : "Add photos"}</b>
          </label>
        )}
      </div>
      <small className="field-help">
        Square photos look best (1000 × 1000 px).{hasGallery ? ` Up to ${max} photos; the first is shown in lists.` : ""}{" "}
        <button type="button" className="link-btn" onClick={() => setShowLink((v) => !v)}>{showLink ? "Hide link box" : "Paste a link instead"}</button>
      </small>
      {showLink && photos.length < max && (
        <div className="photo-link">
          <input value={link} onChange={(e) => setLink(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLink())} placeholder="https://…/photo.jpg" aria-label="Photo link" />
          <button type="button" className="outline-btn" onClick={addLink}>Add</button>
        </div>
      )}
    </div>
  );
}
const GalleryFields = ProductPhotos;

// The home page's small cards (trust points, highlight cards): an emoji, a title and one line each
// switchable: the cards can be hidden on the store, all at once (off / onOffChange) or one by one (card.hidden)
function HomeCardsEditor({ title, help, cards, onChange, names, switchable = false, off = false, onOffChange }) {
  const update = (index, key, value) => onChange(cards.map((card, i) => (i === index ? { ...card, [key]: value } : card)));
  return (
    <>
      <div className="field-section-title home-cards-title">
        <span>{title}</span>
        {switchable && (
          <label className="switch-row">
            <input type="checkbox" checked={!off} onChange={(e) => onOffChange(!e.target.checked)} />
            <span>{off ? "Hidden on your store" : "Shown on your store"}</span>
          </label>
        )}
      </div>
      <p className="field-help">{help}</p>
      {!off && cards.map((card, index) => (
        <div className={`home-card-edit ${card.hidden ? "is-hidden" : ""}`} key={index}>
          <b className="home-card-head">
            <span>{names[index]}</span>
            {switchable && (
              <label className="switch-row small">
                <input type="checkbox" checked={!card.hidden} onChange={(e) => update(index, "hidden", !e.target.checked)} />
                <span>{card.hidden ? "Hidden" : "Shown"}</span>
              </label>
            )}
          </b>
          <div className="home-card-fields">
            <label className="admin-field home-card-icon">Icon<input value={card.icon} maxLength={16} onChange={(e) => update(index, "icon", e.target.value)} placeholder="🌱" aria-label={`${names[index]} icon`} /></label>
            <label className="admin-field">Title<input value={card.title} maxLength={40} onChange={(e) => update(index, "title", e.target.value)} /></label>
          </div>
          <label className="admin-field">Line below<input value={card.text} maxLength={80} onChange={(e) => update(index, "text", e.target.value)} /></label>
        </div>
      ))}
    </>
  );
}

// A colour typed as #rrggbb. Half-typed codes stay in the box and only a complete one is used.
function ColorCodeInput({ value, onChange }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <input
      value={text}
      maxLength={7}
      spellCheck="false"
      aria-label="Colour code"
      onChange={(e) => {
        const typed = e.target.value.trim();
        setText(typed);
        const code = typed.startsWith("#") ? typed : `#${typed}`;
        if (/^#[0-9a-f]{6}$/i.test(code)) onChange(code.toLowerCase());
      }}
      onBlur={() => setText(value)}
    />
  );
}

function TextField({ label, value, onChange, rows = 4, className = "" }) {
  return <label className={`admin-field ${className}`}>{label}<textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} /></label>;
}

// Uploads an image to the server and hands back its URL
// Uploads an image to the server and hands back its URL. kind sets the size limit (banner 5 MB, others 2 MB) and
// how the picture is fitted to its slot before upload (utils/imageFit.js).
// Reads the picture's size and returns a tip when it is too small or a very different shape from the slot, else ""
const sizeTip = (file, slot) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: w, naturalHeight: h } = img;
      const want = slot.width / slot.height;
      if (w < slot.width * 0.6 || h < slot.height * 0.6) resolve(`This image is ${w} × ${h} px, small for a ${slot.label}. It may look blurry: ${slot.width} × ${slot.height} px is best.`);
      else if (Math.abs(w / h - want) / want > 0.25) resolve(`This image is ${w} × ${h} px. It will be cropped from the centre to a ${slot.label} (${slot.width} × ${slot.height} px), so the edges may be cut.`);
      else resolve("");
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(""); };
    img.src = url;
  });

// Checks, compresses and uploads one picture; upload(file) gives its URL, or null after telling the user why not
function useImageUpload(siteId, notify, kind = "product") {
  const [busy, setBusy] = useState(false);
  const limit = IMAGE_LIMITS[kind] || MAX_IMAGE_BYTES;
  const slot = IMAGE_SLOTS[kind];
  const upload = async (file) => {
    if (!file.type.startsWith("image/")) { notify("Choose an image file.", "warn"); return null; }
    // A picture much smaller or of another shape than the slot still uploads, with a tip for a sharper result
    const tip = slot ? await sizeTip(file, slot) : "";
    if (tip) notify(tip, "warn");
    // Bigger photos are compressed instead of refused: here before uploading, and again on the server
    if (file.size > MAX_PHOTO_BYTES) { notify(`This photo is ${bytes(file.size)}. Choose one of up to ${bytes(MAX_PHOTO_BYTES)}.`, "warn"); return null; }
    setBusy(true);
    try {
      const prepared = await prepareUpload(file, kind, limit);
      const { data } = await uploadMedia(siteId, prepared.file, kind);
      notify(`Uploaded ${file.name}${file.size > data.size ? ` (compressed from ${bytes(file.size)} to ${bytes(data.size)})` : ` (${bytes(data.size)})`}. ${bytes(data.leftBytes)} of storage left.`);
      return data.url;
    } catch (err) {
      notify(errorMessage(err, err.message || "Image upload failed."), "error");
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, upload, limit, slot };
}

function ImageUpload({ label, siteId, onUploaded, notify, kind = "product" }) {
  const { busy, upload, limit, slot } = useImageUpload(siteId, notify, kind);
  const choose = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const url = await upload(file);
    if (url) onUploaded(url);
  };
  return (
    <label className="admin-field">
      {label}
      {slot && (
        <span className="image-size-hint">
          📐 Best size: <b>{slot.width} × {slot.height} px</b> ({slot.hint}) · at least {Math.round(slot.width * 0.6)} × {Math.round(slot.height * 0.6)} px
        </span>
      )}
      {kind === "logo" && <span className="image-size-hint">📐 Best: a square PNG of at least <b>512 × 512 px</b> with a transparent background</span>}
      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={choose} disabled={busy} />
      <small className="field-help">
        {busy ? "Compressing and uploading…" : `JPG, PNG, WebP or GIF · Photos over 2 MB are compressed to half their size or less (up to ${bytes(limit)} stored)${slot ? ` · Cropped from the centre to a ${slot.label} and resized to fit` : ""}`}
      </small>
    </label>
  );
}

// How much of the owner's storage (shared by all their stores) the uploads use. The bar turns amber, then red, as it
// fills up. There is no limit on the number of images.
function StorageMeter({ media }) {
  const share = media.limit ? media.used / media.limit : 0;
  const level = share >= 1 ? "is-full" : share >= 0.7 ? "is-high" : "";
  return (
    <div className={`storage-meter ${level}`}>
      <div className="storage-meter-top">
        <b>{bytes(media.used)} of {bytes(media.limit)} <span>used</span></b>
        <em>{share >= 1 ? "Storage full" : `${media.count} image${media.count === 1 ? "" : "s"}`}</em>
      </div>
      {/* a little storage still shows a sliver, so the bar never looks empty once something is stored */}
      <div className="storage-track"><span style={{ width: `${share ? Math.min(100, Math.max(1, share * 100)) : 0}%` }} /></div>
      <div className="storage-meter-foot">
        <span>Shared by all your stores</span>
        <span>{bytes(Math.max(0, media.limit - media.used))} free</span>
      </div>
      {media.held > 0 && <small className="storage-held">+ {bytes(media.held)} of photos kept for a data migration's backup. These use the extra {bytes(media.backupLimit)} for backups, not your storage, and are deleted after 2 days.</small>}
      <small>Images are compressed when uploaded. A replaced or removed image is deleted and frees its space.</small>
    </div>
  );
}

// This website's emails this month (login codes, order confirmations, ...): the monthly allowance plus emails bought
// as add-ons. Same bar as the storage, amber from 70% and red when full.
function EmailMeter({ emails }) {
  if (!emails) return null;
  const share = emails.emailLimit ? emails.emailsSent / emails.emailLimit : 0;
  const level = share >= 1 ? "is-full" : share >= emails.warnAt ? "is-high" : "";
  const n = (value) => Number(value || 0).toLocaleString("en-IN");
  const resets = new Date(`${emails.emailsResetOn}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return (
    <div className={`storage-meter ${level}`}>
      <div className="storage-meter-top">
        <b>{n(emails.emailsSent)} of {n(emails.emailLimit)} <span>emails this month</span></b>
        <em>{share >= 1 ? "Limit reached" : `${Math.floor(share * 100)}%`}</em>
      </div>
      <div className="storage-track"><span style={{ width: `${share ? Math.min(100, Math.max(1, share * 100)) : 0}%` }} /></div>
      <div className="storage-meter-foot">
        <span>{n(emails.monthlyEmails)} a month, back to 0 on {resets}</span>
        <span>{emails.extraEmails > 0 ? `+ ${n(emails.extraEmails)} bought emails left` : `${n(Math.max(0, emails.emailLimit - emails.emailsSent))} left`}</span>
      </div>
      <small>Login codes, order confirmations and other emails this website sends. At the limit order confirmations stop; login codes still go out.</small>
    </div>
  );
}

// Storefront editor (/admin/sites/:siteId/edit): store details, banners, categories, products and images.
// editor/siteData.js loads the site and saves only what changed.
export default function WebsiteEditor() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isSuper = user.role === "superadmin";

  const [site, setSite] = useState(null); // the draft being edited
  const [meta, setMeta] = useState({ slug: siteId, subdomain: "" });
  const base = useRef(null); // what the server had when we last loaded or saved
  const [loadError, setLoadError] = useState("");
  const [mode, setMode] = useState("edit"); // opens on the full-width editor; Side by side and Preview are a click away
  // Side by side shows the section menu as icons only: hovering or focusing one shows its name beside it
  const [tip, setTip] = useState(null); // { text, top, left }
  const showTip = (text) => (e) => {
    if (mode !== "split") return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ text, top: r.top + r.height / 2, left: r.right + 10 });
  };
  const hideTip = () => setTip(null);
  const [searchParams] = useSearchParams();
  // Shipping & tax: Super Admin, the store owner, and staff the owner gave Store settings to
  const tabs = EDITOR_TABS.filter((t) => t !== "Shipping & tax" || SETTINGS_ROLES.includes(user.role) || (user.role === "staff" && can(user, "settings")));
  const [activeSection, setActiveSection] = useState(() => (tabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "Content"));
  const [policyTab, setPolicyTab] = useState("privacyPolicy");
  const [saved, setSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState([]);
  const [media, setMedia] = useState({ count: 0, used: 0, limit: 2 * 1024 * 1024 * 1024, held: 0, backupLimit: 1024 * 1024 * 1024 });
  const [productModal, setProductModal] = useState(false);
  const [draftProduct, setDraftProduct] = useState(EMPTY_PRODUCT);
  const [productSaving, setProductSaving] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [previewKey, setPreviewKey] = useState(0); // bumped to reload the store preview (after every save)
  const tabsRef = useRef(null);
  const [bulkBusy, setBulkBusy] = useState(""); // the bulk delete running ("products", ...), for its button
  const scrollTabs = (direction) => tabsRef.current && tabsRef.current.scrollBy({ left: direction * 220, behavior: "smooth" });
  const [justAdded, setJustAdded] = useState(null); // the offer product just added: scrolled to and highlighted
  useEffect(() => {
    if (justAdded === null) return undefined;
    const card = document.getElementById(`offer-item-${justAdded}`);
    if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = window.setTimeout(() => setJustAdded(null), 3000);
    return () => window.clearTimeout(timer);
  }, [justAdded]);

  const notify = toast;
  const blogApi = useMemo(() => storeBlog(siteId), [siteId]);
  // "Preview" next to Save shows the full-width preview of the unsaved draft; pressed again it returns to editing
  const previewing = mode === "preview";
  const togglePreview = () => setMode(previewing ? "edit" : "preview");
  const previewButton = (
    <button className={`editor-preview-btn ${previewing ? "active" : ""}`} onClick={togglePreview} aria-pressed={previewing}>
      {previewing ? "✎ Back to editing" : "👁 Preview"}
    </button>
  );

  const refreshMedia = useCallback(() => {
    getMedia(siteId)
      .then(({ data }) => setMedia({ count: data.count, used: data.usedBytes, limit: data.limitBytes, held: data.heldBytes || 0, backupLimit: data.backupLimitBytes, emails: data.emails }))
      .catch(() => {});
  }, [siteId]);
  // A blog post's cover goes to the store's image library like any other upload
  const BlogCover = useCallback(({ onUploaded }) => <ImageUpload kind="banner" label="Or upload a cover" siteId={siteId} notify={toast} onUploaded={(url) => { onUploaded(url); refreshMedia(); }} />, [siteId, refreshMedia]);

  const load = useCallback(async () => {
    try {
      const loaded = await loadSite(siteId, isSuper);
      base.current = loaded.base;
      setSite(loaded.draft);
      setMeta(loaded.meta);
      setSaved(true);
      setLoadError("");
    } catch (err) {
      setLoadError(errorMessage(err));
    }
  }, [siteId, isSuper]);

  useEffect(() => { load(); refreshMedia(); }, [load, refreshMedia]);

  // ?add=1 (the Overview's and the setup modal's "Add products"): opens the new product form once the site has loaded
  useEffect(() => { track("tab", `Website editor: ${activeSection}`, siteId); }, [activeSection, siteId]); // Admin Tracker
  const autoAdd = useRef(searchParams.get("add") === "1");
  useEffect(() => {
    if (!site || !autoAdd.current) return;
    autoAdd.current = false;
    if (!site.categories.length) return;
    const variants = site.template === "clothing" ? { sizes: SIZE_PRESETS.Women, colors: [], gallery: [] } : site.template === "gifts" ? { sizes: [], colors: [], gallery: [], optionLabel: "" } : {};
    setDraftProduct({ ...EMPTY_PRODUCT, ...variants, id: `new_${Date.now()}`, cat: site.categories[0] });
    setProductModal(true);
  }, [site]);

  // Warn before closing the tab with unsaved edits
  useEffect(() => {
    if (saved) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [saved]);

  const categories = site ? site.categories : [];
  // The Products tab lists products under their category; a search keeps only the matching products (and categories)
  const searchText = productSearch.trim().toLowerCase();
  const productGroups = useMemo(() => {
    if (!site) return [];
    const matches = (p) => !searchText || [p.name, p.desc, p.cat, ...(p.tags || [])].some((x) => String(x || "").toLowerCase().includes(searchText));
    const groups = categories.map((name) => ({ name, products: site.products.filter((p) => p.cat === name && matches(p)) }));
    const other = site.products.filter((p) => !categories.includes(p.cat) && matches(p));
    const all = other.length ? [...groups, { name: "Without a category", products: other }] : groups;
    return searchText ? all.filter((g) => g.products.length) : all;
  }, [site, categories, searchText]);
  const productCounts = useMemo(() => {
    const counts = {};
    (site ? site.products : []).forEach((p) => { counts[p.cat] = (counts[p.cat] || 0) + 1; });
    return counts;
  }, [site]);
  const effectiveMedia = useMemo(() => {
    const imageSet = new Set();
    (site?.products || []).forEach((p) => {
      if (p.img) imageSet.add(p.img);
      if (Array.isArray(p.gallery)) p.gallery.forEach((g) => { if (g) imageSet.add(g); });
    });
    (site?.heroBanners || []).forEach((b) => { if (b.image) imageSet.add(b.image); });
    if (site?.logo) imageSet.add(site.logo);

    const totalImages = Math.max(media.count || 0, imageSet.size);
    if (totalImages === 0) return media;

    const estimatedBytes = imageSet.size * 180 * 1024;
    const effectiveUsed = Math.max(media.used || 0, estimatedBytes);

    return {
      ...media,
      count: totalImages,
      used: effectiveUsed,
    };
  }, [media, site?.products, site?.heroBanners, site?.logo]);
  if (loadError) {
    return (
      <div className="login-shell"><div className="login-card">
        <h1>Can't open the editor</h1>
        <div className="form-error">{loadError}</div>
        <button className="primary-btn" onClick={() => navigate("/admin")}>← Back to dashboard</button>
      </div></div>
    );
  }
  if (!site) return <div className="login-shell"><div className="login-card"><Loader label="Loading website" /></div></div>;

  const edit = (updater) => { setSite(updater); setSaved(false); };
  const change = (key, value) => edit((s) => ({ ...s, [key]: value }));

  // ---- hero banners ----
  const updateHero = (index, key, value) => edit((s) => ({ ...s, heroBanners: s.heroBanners.map((b, i) => (i === index ? { ...b, [key]: value } : b)) }));
  const addHero = () => {
    if (site.heroBanners.length >= 4) return notify("You can add a maximum of 4 hero banners.", "warn");
    change("heroBanners", [...site.heroBanners, { _key: `hero_${Date.now()}`, title: "New banner headline", description: "Add a short description for this banner.", image: "", imageOnly: false }]);
  };
  const removeHero = (index) => {
    const next = site.heroBanners.filter((_, i) => i !== index);
    change("heroBanners", next.length ? next : [{ _key: `hero_${Date.now()}`, title: "", description: "", image: "" }]);
  };

  // ---- delete in bulk (saved straight away on the server, then the editor reloads) ----
  const BULK = {
    samples: ["Delete sample products", "Delete the sample / placeholder products and categories your store started with? Your own products stay."],
    products: ["Delete all products", "Delete ALL products of this store? Categories and banners stay."],
    categories: ["Delete all categories", "Delete ALL categories and every product in them?"],
    everything: ["Delete everything", "Delete ALL products, categories and banner images? Your store will be empty, ready for your own products."],
  };
  const bulkDelete = async (what) => {
    if (!window.confirm(`${BULK[what][1]}\n\nThis cannot be undone.`)) return;
    if (!saved && !window.confirm("You have unsaved changes. They will be lost. Continue?")) return;
    setBulkBusy(what);
    try {
      const { data } = await clearCatalog(siteId, what);
      const r = data.removed || {};
      notify(`Deleted ${r.products || 0} products${r.categories ? `, ${r.categories} categories` : ""}${r.banners ? ` and ${r.banners} banner images` : ""}.`);
      await load();
      refreshMedia();
      setPreviewKey((k) => k + 1);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBulkBusy("");
    }
  };
  const bulkDeleteBox = (whats) => (
    <div className="bulk-delete">
      <b>Delete in bulk</b>
      <p>Start fresh with only your own products. These delete straight away and can't be undone.</p>
      <div className="bulk-delete-actions">
        {whats.map((what) => (
          <button type="button" key={what} disabled={Boolean(bulkBusy)} onClick={() => bulkDelete(what)}>
            🗑 {bulkBusy === what ? "Deleting…" : BULK[what][0]}
          </button>
        ))}
      </div>
    </div>
  );

  // ---- products ----
  // Sizes and colours are for clothing stores only (website editor -> Design -> store type)
  const isClothing = Boolean(site && site.template === "clothing");
  // Gift stores: named options, personalisation and more photos (the clothing store's sizes and photos, for gifts)
  const isGifts = Boolean(site && site.template === "gifts");
  const isSelfcare = Boolean(site && site.template === "selfcare");
  // Every store type can sell a product in sizes (or lengths) and colours; clothing, gift and soap stores get
  // their own presets, the others the general ones
  const isGeneral = !(isClothing || isGifts || isSelfcare);
  const hasOptions = true;

  const updateProduct = (id, key, value) => edit((s) => ({ ...s, products: s.products.map((p) => (p.id === id ? { ...p, [key]: value } : p)) }));
  // Deleted straight away (no Save changes needed); other unsaved edits stay
  const removeProduct = async (id) => {
    const product = site.products.find((p) => p.id === id);
    if (!window.confirm(`Delete "${(product && product.name) || "this product"}"? It is removed from your store now.`)) return false;
    try {
      setSite(await deleteProductNow(siteId, site, base.current, id));
      notify(`"${(product && product.name) || "Product"}" deleted.`);
      return true;
    } catch (err) {
      notify(errorMessage(err, "Could not delete the product. Try again."), "error");
      return false;
    }
  };
  const addProduct = () => {
    if (!categories.length) return notify("Add a category first (Categories tab). Every product belongs to a category.", "warn");
    // Clothing: a new product starts in the usual sizes, which the owner can change
    const variants = isClothing ? { sizes: SIZE_PRESETS.Women, colors: [], gallery: [] } : isGifts ? { sizes: [], colors: [], gallery: [], optionLabel: "" } : { sizes: [], colors: [], optionLabel: "" };
    setDraftProduct({ ...EMPTY_PRODUCT, ...variants, id: `new_${Date.now()}`, cat: categories[0] });
    setProductModal(true);
  };
  const openProduct = (p) => {
    setDraftProduct({ ...EMPTY_PRODUCT, ...p });
    setProductModal(true);
  };
  // Add / Edit product saves to the store at once. A product in a category that is not saved yet waits for Save
  // changes with the category.
  const saveProduct = async () => {
    const name = draftProduct.name.trim();
    if (!name) return notify("Enter a product name first.", "warn");
    if (!draftProduct.cat) return notify("Select a category first.", "warn");
    if (!(Number(draftProduct.price) >= 0)) return notify("Enter a price.", "warn");
    const product = { ...draftProduct, name };
    if (!site.categoryIds[site.categories.indexOf(product.cat)]) {
      edit((s) => ({ ...s, products: s.products.some((p) => p.id === product.id) ? s.products.map((p) => (p.id === product.id ? product : p)) : [...s.products, product] }));
      setProductModal(false);
      return notify(`"${name}" is ready. Press Save changes to add the new category "${product.cat}" and the product to your store.`);
    }
    setProductSaving(true);
    try {
      const result = await saveProductNow(siteId, site, base.current, product);
      setSite(result.draft);
      setProductModal(false);
      refreshMedia();
      setPreviewKey((k) => k + 1);
      notify(result.placeholdersRemoved
        ? `"${name}" is live on your store. The ${result.placeholdersRemoved} sample products were removed now that you have your own.`
        : `"${name}" is saved and live on your store.`);
    } catch (err) {
      notify(errorMessage(err, "Could not save the product. Check the details and try again."), "error");
    } finally {
      setProductSaving(false);
    }
    return undefined;
  };

  // ---- featured products: always the owner's own list. A store that never chose (null) starts from the newest
  // products shown today, and the first change saves that list as theirs. ----
  const setFeatured = (ids) => change("featuredProducts", ids);
  const moveFeatured = (index, step) => {
    const ids = featuredItems.map((p) => p.id);
    [ids[index], ids[index + step]] = [ids[index + step], ids[index]];
    setFeatured(ids);
  };

  // ---- offers ----
  const setOffers = (patch) => edit((s) => ({ ...s, offers: { ...s.offers, ...patch } }));
  // A product put on offer is added at the end of the list, scrolled to and highlighted, ready for its offer price
  const addOffer = (id) => {
    const product = site.products.find((p) => p.id === id);
    setOffers({ products: [...site.offers.products, id] });
    setJustAdded(id);
    if (product) notify(`"${product.name}" added to ${site.offers.title || "Offers"}. Set its offer price in the highlighted card.`);
  };
  // A combo pack is saved by itself; it joins the draft without a reload, so other unsaved edits stay
  const comboSaved = async (product) => {
    try {
      setSite(await mergeSavedProduct(siteId, site, base.current, product));
      if (!site.offers.products.includes(product.id)) setJustAdded(product.id);
    } catch {
      notify("The combo was saved. Reload the editor to see it.", "warn");
    }
  };
  const moveOffer = (index, step) => {
    const ids = [...site.offers.products];
    [ids[index], ids[index + step]] = [ids[index + step], ids[index]];
    setOffers({ products: ids });
  };

  // ---- categories (kept as a list of names, with the server ids alongside) ----
  const updateCategory = (index, value) =>
    edit((s) => {
      const old = s.categories[index];
      return {
        ...s,
        categories: s.categories.map((c, i) => (i === index ? value : c)),
        inactiveCategories: s.inactiveCategories.map((c) => (c === old ? value : c)),
        products: s.products.map((p) => (p.cat === old ? { ...p, cat: value } : p)),
      };
    });
  const addCategory = () => edit((s) => ({ ...s, categories: [...s.categories, `New category ${s.categories.length + 1}`], categoryIds: [...s.categoryIds, null] }));
  const removeCategory = (index) => {
    const name = site.categories[index];
    if (site.products.some((p) => p.cat === name)) return notify("Move products to another category before removing this category.", "warn");
    edit((s) => ({
      ...s,
      categories: s.categories.filter((_, i) => i !== index),
      categoryIds: s.categoryIds.filter((_, i) => i !== index),
      inactiveCategories: s.inactiveCategories.filter((c) => c !== name),
    }));
  };
  const toggleCategory = (cat) =>
    change("inactiveCategories", site.inactiveCategories.includes(cat) ? site.inactiveCategories.filter((x) => x !== cat) : [...site.inactiveCategories, cat]);

  // ---- save ----
  const save = async () => {
    setSaving(true);
    setProblems([]);
    const result = await saveSite(siteId, site, base.current, isSuper);
    if (result.errors.length) {
      // Keep the draft, but remember the real ids of what was created so a retry does not duplicate it
      setSite((s) => ({
        ...s,
        products: s.products.map((p) => result.createdProducts[p.id] || p),
        categoryIds: s.categoryIds.map((id, i) => id || result.createdCategories[s.categories[i]] || null),
      }));
      setProblems(result.errors);
      notify("Some changes could not be saved. See the list in the editor.", "error");
    } else {
      await load();
      refreshMedia();
      setPreviewKey((k) => k + 1); // the store preview shows the saved changes
      notify(result.placeholdersRemoved
        ? `Website changes saved. The ${result.placeholdersRemoved} sample products were removed now that you have added your own.`
        : "Website changes saved.");
    }
    setSaving(false);
  };

  const leave = () => {
    if (saved || window.confirm("You have unsaved changes. Leave without saving?")) navigate("/admin");
  };

  const productById = new Map(site.products.map((p) => [p.id, p]));
  const featuredItems = site.featuredProducts ? site.featuredProducts.map((id) => productById.get(id)).filter(Boolean) : newestProducts(site.products);
  const featurable = site.products.filter((p) => typeof p.id === "number" && !featuredItems.includes(p));
  const offerItems = site.offers.products.map((id) => productById.get(id)).filter(Boolean);
  const offerable = site.products.filter((p) => typeof p.id === "number" && !site.offers.products.includes(p.id));
  const percentOff = (p) => (Number(p.originalPrice) > Number(p.price) ? Math.round((1 - Number(p.price) / Number(p.originalPrice)) * 100) : 0);
  const previewDomain = site.domain || meta.subdomain;
  const themeColor = site.color || "#31862f";
  const customColor = !THEME_COLORS.includes(themeColor.toLowerCase());

  // One line per product (photo, name, price, stock); a tap opens the same form as Add product
  const productFields = (p) => (
    <div className="product-row" key={p.id}>
      <button type="button" className="product-row-open" onClick={() => openProduct(p)} aria-label={`Edit ${p.name || "product"}`}>
        {p.img ? <img src={p.img} alt="" /> : <span className="product-row-noimg" aria-hidden="true">📷</span>}
        <span className="product-row-main">
          <b>{p.name || "Untitled product"}{typeof p.id === "string" && <em className="product-row-new">Not saved</em>}</b>
          <small>{(p.sizePrices || []).length > 0 && "From "}₹{p.price ?? 0}{Number(p.originalPrice) > Number(p.price) && <del>₹{p.originalPrice}</del>} · {p.stock ?? 0} in stock</small>
        </span>
        <span className={`product-row-status ${p.availability === "Out of stock" ? "out" : ""}`}>{p.availability || "In stock"}</span>
      </button>
      <div className="product-row-actions">
        <button type="button" className="product-row-btn" onClick={() => openProduct(p)}>✏️ <span>Edit</span></button>
        <button type="button" className="product-row-btn danger" onClick={() => removeProduct(p.id)} aria-label={`Delete ${p.name || "product"}`}>🗑️</button>
      </div>
    </div>
  );

  return (
    <div className="editor-shell">
      <header className="editor-topbar">
        <button className="back-link" onClick={leave}>← Dashboard</button>
        <div className="editor-title">
          <span className="editor-site-mark">{(site.name || "S").slice(0, 1)}</span>
          <div><b>{site.name}</b><small>{previewDomain} · Website editor</small></div>
        </div>
        <div className="editor-controls">
          <div className="mode-switch">
            <button className={mode === "edit" ? "active" : ""} onClick={() => setMode("edit")}>Editor full</button>
            <button className={mode === "split" ? "active" : ""} onClick={() => setMode("split")}>Side by side</button>
            <button className={mode === "preview" ? "active" : ""} onClick={() => setMode("preview")}>Preview full</button>
          </div>
          {previewButton}
          <button className="editor-save" onClick={save} disabled={saving || saved}>{saving ? <><Spinner /> Saving</> : saved ? "✓ Saved" : "✓ Save changes"}</button>
        </div>
      </header>

      <div className={`editor-workspace mode-${mode}`}>
        <aside className="editor-panel">
          {/* The section menu: a side menu next to the fields on a computer, a row that scrolls sideways (with
              arrows) on a phone */}
          <div className="editor-body">
          <div className="editor-tabs-wrap">
          <button type="button" className="editor-tabs-arrow prev" onClick={() => scrollTabs(-1)} aria-label="Scroll sections left">‹</button>
          <nav className="editor-tabs" aria-label="Editor sections" ref={tabsRef}>
            <div className="editor-tabs-status"><span className="eyebrow">WEBSITE EDITOR</span><span className={`live-dot ${saved ? "" : "is-unsaved"}`}>● {saved ? "Up to date" : "Unsaved"}</span></div>
            {TAB_GROUPS.map((group) => {
              const shown = group.tabs.filter((t) => tabs.includes(t));
              return shown.length > 0 && (
                <div className="editor-tab-group" key={group.label}>
                  <span className="editor-tab-group-label">{group.label}</span>
                  <div className="editor-tab-group-items">
                    {shown.map((t) => (
                      <button className={activeSection === t ? "active" : ""} key={t} onClick={() => setActiveSection(t)} aria-current={activeSection === t ? "page" : undefined}
                        aria-label={t} onMouseEnter={showTip(t)} onMouseLeave={hideTip} onFocus={showTip(t)} onBlur={hideTip}>
                        <span aria-hidden="true">{TAB_INFO[t][0]}</span>{t}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
          <button type="button" className="editor-tabs-arrow next" onClick={() => scrollTabs(1)} aria-label="Scroll sections right">›</button>
          </div>
          {tip && mode === "split" && <span className="editor-tab-tip" role="tooltip" style={{ top: tip.top, left: tip.left }}>{tip.text}</span>}
          <div className="editor-fields">
            <div className="editor-section-intro">
              <span aria-hidden="true">{TAB_INFO[activeSection][0]}</span>
              <div><h3>{activeSection}</h3><p>{TAB_INFO[activeSection][1]}</p></div>
            </div>
            {problems.length > 0 && (
              <div className="admin-error">
                <b>Not everything could be saved:</b>
                <ul>{problems.map((p) => <li key={p}>{p}</li>)}</ul>
                Your edits are still here. Fix the problem and press Save changes again.
              </div>
            )}

            {activeSection === "Content" && (
              <>
                <div className="field-section-title">Announcement bar</div>
                <Field label="Announcement text" value={site.announcement} onChange={(v) => change("announcement", v)} />
                <div className="hero-editor-heading">
                  <div><div className="field-section-title">Hero banners</div><p>Add up to 4 rotating banners. Each banner has its own headline, description and image.</p></div>
                  <span>{site.heroBanners.length}/4</span>
                </div>
                {site.heroBanners.map((banner, index) => (
                  <div className="hero-edit-card" key={banner._key}>
                    <div className="hero-edit-card-head"><b>Banner {index + 1}</b>{site.heroBanners.length > 1 && <button className="danger-text-btn" onClick={() => removeHero(index)}>Remove</button>}</div>
                    {/* Image with text (title, description and buttons over the picture) or the picture alone */}
                    <div className="banner-style-toggle" role="radiogroup" aria-label={`Banner ${index + 1} style`}>
                      <button type="button" role="radio" aria-checked={!banner.imageOnly} className={!banner.imageOnly ? "active" : ""} onClick={() => updateHero(index, "imageOnly", false)}>
                        <span aria-hidden="true">🖼️🅣</span> Image with text
                      </button>
                      <button type="button" role="radio" aria-checked={Boolean(banner.imageOnly)} className={banner.imageOnly ? "active" : ""} onClick={() => updateHero(index, "imageOnly", true)}>
                        <span aria-hidden="true">🖼️</span> Image only
                      </button>
                    </div>
                    {banner.imageOnly ? (
                      <p className="field-help banner-style-note">
                        Only the picture shows on your store, without text or buttons; a tap on it opens your products. Use an image that already has your
                        message on it.{!banner.image && <b> Upload an image for this banner.</b>}
                      </p>
                    ) : (
                      <>
                        <Field label="Banner title" value={banner.title} onChange={(v) => updateHero(index, "title", v)} />
                        <TextField label="Banner description" value={banner.description} onChange={(v) => updateHero(index, "description", v)} />
                      </>
                    )}
                    <Field label="Banner image URL" value={banner.image} onChange={(v) => updateHero(index, "image", v)} placeholder="https://example.com/banner.jpg" />
                    <ImageUpload kind="banner" label="Or upload banner image" siteId={siteId} notify={notify} onUploaded={(url) => { updateHero(index, "image", url); refreshMedia(); }} />
                    <div className="image-url-preview">
                      {banner.image ? <img src={banner.image} alt={`Banner ${index + 1} preview`} /> : <div className="image-url-empty">🖼️ No image yet</div>}
                      <span>Banner {index + 1} image preview</span>
                      {banner.image && <button type="button" className="danger-text-btn" onClick={() => updateHero(index, "image", "")}>Remove image</button>}
                    </div>
                    {/* Phones show the hero tall and narrow: a tall picture made for them stops the wide one being cut off */}
                    <Field label="Mobile image URL (optional)" value={banner.mobileImage || ""} onChange={(v) => updateHero(index, "mobileImage", v)} placeholder="https://example.com/banner-mobile.jpg" />
                    <ImageUpload kind="banner" label="Or upload mobile image" siteId={siteId} notify={notify} onUploaded={(url) => { updateHero(index, "mobileImage", url); refreshMedia(); }} />
                    <small className="field-help">Shown on phones instead of the banner image. Use a tall picture (e.g. 900 × 1400) with the main subject in the top half; the text sits on the bottom half.</small>
                    {banner.mobileImage && (
                      <div className="image-url-preview image-url-preview-tall">
                        <img src={banner.mobileImage} alt={`Banner ${index + 1} mobile preview`} />
                        <span>Banner {index + 1} mobile image</span>
                        <button type="button" className="danger-text-btn" onClick={() => updateHero(index, "mobileImage", "")}>Remove image</button>
                      </div>
                    )}
                  </div>
                ))}
                <button className="outline-btn full-btn hero-add-btn" disabled={site.heroBanners.length >= 4} onClick={addHero}>＋ Add banner {site.heroBanners.length >= 4 ? "(maximum reached)" : ""}</button>
                {bulkDeleteBox(["everything"])}
                <HomeCardsEditor
                  title="Trust points"
                  help="The 4 short points shown under the banner, e.g. delivery or support. Use one emoji as the icon."
                  names={["Point 1", "Point 2", "Point 3", "Point 4"]}
                  cards={site.highlights}
                  onChange={(cards) => change("highlights", cards)}
                  switchable
                  off={Boolean(site.highlightsOff)}
                  onOffChange={(value) => change("highlightsOff", value)}
                />
                <HomeCardsEditor
                  title="Highlight cards"
                  help="The 3 coloured cards below the trust points."
                  names={["Card 1 (green)", "Card 2 (peach)", "Card 3 (lilac)"]}
                  cards={site.spotlights}
                  onChange={(cards) => change("spotlights", cards)}
                />
                <ShopByEditor value={site.shopBy} categories={categories} onChange={(value) => change("shopBy", value)} />
                <div className="field-section-title">About section</div>
                <TextField label="About description" value={site.about} onChange={(v) => change("about", v)} />
              </>
            )}

            {activeSection === "Products" && (
              <>
                {/* Stays at the top while the list scrolls (a round button at the bottom on phones) */}
                <div className="products-toolbar">
                  <div className="product-search">
                    <input type="search" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search products by name, category or tag…" aria-label="Search products" />
                    <small>{searchText ? `${productGroups.reduce((n, g) => n + g.products.length, 0)} found` : `${site.products.length} products`}</small>
                  </div>
                  <button className="primary-btn products-add-btn" onClick={addProduct}><span aria-hidden="true">＋</span> Add product</button>
                </div>
                <StorageMeter media={effectiveMedia} />
                <CatalogTransfer siteId={siteId} notify={notify} saved={saved} onImported={load} />
                {searchText && !productGroups.length && <p className="field-help">No products match "{productSearch.trim()}".</p>}
                {/* One section per category, in the Categories tab's order; products whose category is gone come last.
                    While searching every matching category is open. */}
                {productGroups.map((group, index) => (
                  <details className="product-group" key={group.name} open={Boolean(searchText) || index === 0}>
                    <summary>
                      <span className="product-group-name">{group.name}</span>
                      <span className="product-group-count">{group.products.length} {group.products.length === 1 ? "product" : "products"}</span>
                    </summary>
                    {group.products.length ? group.products.map(productFields) : <p className="field-help product-group-empty">No products in this category yet.</p>}
                  </details>
                ))}
                {site.products.length > 0 && bulkDeleteBox(["samples", "products", "everything"])}
              </>
            )}

            {activeSection === "Featured" && (
              <>
                <div className="products-editor-heading">
                  <div><h3>Featured products</h3><p>Shown in a grid near the top of your home page.</p></div>
                  <span>{featuredItems.length}/{FEATURED_LIMIT}</span>
                </div>
                <label className="admin-field">Add a product
                  <select value="" disabled={featuredItems.length >= FEATURED_LIMIT} onChange={(e) => e.target.value && setFeatured([...featuredItems.map((p) => p.id), Number(e.target.value)])}>
                    <option value="">{featuredItems.length >= FEATURED_LIMIT ? `Maximum ${FEATURED_LIMIT} reached. Remove one to add another.` : "Select a product to feature…"}</option>
                    {featurable.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.cat}</option>)}
                  </select>
                </label>
                <small className="field-help">Use the arrows to change the order. New products can be featured after you save them.</small>
                <ol className="featured-list">
                  {featuredItems.map((p, i) => (
                    <li key={p.id}>
                      <img src={p.img} alt="" />
                      <div><b>{p.name}</b><small>{p.cat} · ₹{p.price}</small></div>
                      <span className="featured-actions">
                        <button aria-label={`Move ${p.name} up`} disabled={i === 0} onClick={() => moveFeatured(i, -1)}>↑</button>
                        <button aria-label={`Move ${p.name} down`} disabled={i === featuredItems.length - 1} onClick={() => moveFeatured(i, 1)}>↓</button>
                        <button className="danger-text-btn" onClick={() => setFeatured(featuredItems.map((x) => x.id).filter((id) => id !== p.id))}>Remove</button>
                      </span>
                    </li>
                  ))}
                </ol>
                {!featuredItems.length && <p className="field-help">No products are featured, so the Featured section is hidden on your store.</p>}
              </>
            )}

            {activeSection === "Offers" && (
              <>
                <div className="products-editor-heading">
                  <div><h3>Offers</h3><p>Discounted products, shown in their own row on your home page and as a link in the menu.</p></div>
                  <span>{offerItems.length} items</span>
                </div>
                <Field label="Section name" value={site.offers.title} onChange={(v) => setOffers({ title: v.slice(0, 40) })} placeholder="Offers, Diwali deals, Clearance sale…" />
                {/* 1. Pick a product: it goes to the end of the list below, which scrolls to it and highlights it */}
                <div className="offer-add-card">
                  <label className="admin-field">Put a product on offer
                    <select value="" disabled={site.offers.products.length >= OFFERS_LIMIT} onChange={(e) => e.target.value && addOffer(Number(e.target.value))}>
                      <option value="">{site.offers.products.length >= OFFERS_LIMIT ? `Maximum ${OFFERS_LIMIT} reached` : "Select a product…"}</option>
                      {offerable.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.cat} · ₹{p.price}</option>)}
                    </select>
                  </label>
                  <small className="field-help">It is added to "On offer now" below. Set its offer price there, lower than the original price, to show the discount. New products can be put on offer after you save them.</small>
                </div>
                {/* 2. Everything on offer, in the order shown on the store, each with its prices and offer text */}
                <div className="offer-list-heading">
                  <b>On offer now</b>
                  <span>{offerItems.length} {offerItems.length === 1 ? "product" : "products"} · use the arrows to change the order</span>
                </div>
                <ol className="featured-list offer-list">
                  {offerItems.map((p, i) => (
                    <li key={p.id} id={`offer-item-${p.id}`} className={justAdded === p.id ? "just-added" : ""}>
                      <span className="offer-index">{i + 1}</span>
                      <img src={p.img} alt="" />
                      <div>
                        <b>{p.name}{justAdded === p.id && <em className="product-row-new">Just added</em>}</b>
                        <div className="offer-prices">
                          <label>Original ₹<input type="number" min="0" value={p.originalPrice ?? 0} onChange={(e) => updateProduct(p.id, "originalPrice", Number(e.target.value))} /></label>
                          <label>Offer ₹<input type="number" min="0" value={p.price ?? 0} onChange={(e) => updateProduct(p.id, "price", Number(e.target.value))} /></label>
                          {percentOff(p) > 0 ? <span className="offer-badge">{percentOff(p)}% off</span> : <span className="offer-badge none">No discount</span>}
                        </div>
                        {/* Optional words shown on this product in the store's Offers section */}
                        <div className="offer-text-fields">
                          <label>Offer title <em>optional</em><input value={p.offerTitle || ""} maxLength={40} onChange={(e) => updateProduct(p.id, "offerTitle", e.target.value)} placeholder="e.g. Diwali deal" /></label>
                          <label>Offer description <em>optional</em><input value={p.offerNote || ""} maxLength={120} onChange={(e) => updateProduct(p.id, "offerNote", e.target.value)} placeholder="e.g. Free ceramic pot with this plant" /></label>
                        </div>
                      </div>
                      <span className="featured-actions">
                        <button aria-label={`Move ${p.name} up`} disabled={i === 0} onClick={() => moveOffer(i, -1)}>↑</button>
                        <button aria-label={`Move ${p.name} down`} disabled={i === offerItems.length - 1} onClick={() => moveOffer(i, 1)}>↓</button>
                        <button className="danger-text-btn" onClick={() => setOffers({ products: site.offers.products.filter((id) => id !== p.id) })}>Remove</button>
                      </span>
                    </li>
                  ))}
                </ol>
                {!offerItems.length && <p className="field-help">No products are on offer, so the Offers section is hidden on your store.</p>}
                {/* 3. Combo packs: saved at once, and added to the list above */}
                <ComboBuilder siteId={siteId} products={site.products} notify={notify} onSaved={comboSaved} />
              </>
            )}

            {activeSection === "SEO" && (
              <SeoFields seo={site.seo} defaults={site.seoDefaults} domain={previewDomain} onChange={(patch) => edit((x) => ({ ...x, seo: { ...x.seo, ...patch } }))} />
            )}

            {activeSection === "Categories" && (
              <>
                <div className="field-section-title">Product categories</div>
                <p className="field-help">Products are grouped under the category selected in each product editor. Rename or add categories here. Inactive categories are hidden from shoppers.</p>
                {site.categories.map((c, i) => (
                  <div className="category-edit-row" key={site.categoryIds[i] || `new-${i}`}>
                    <input aria-label={`Category ${i + 1}`} value={c} onChange={(e) => updateCategory(i, e.target.value)} />
                    <span className="category-count">{productCounts[c] || 0} {productCounts[c] === 1 ? "product" : "products"}</span>
                    <button className={`category-status-btn ${site.inactiveCategories.includes(c) ? "inactive" : ""}`} onClick={() => toggleCategory(c)}>{site.inactiveCategories.includes(c) ? "Inactive" : "Active"}</button>
                    <button className="danger-text-btn" onClick={() => removeCategory(i)}>Remove</button>
                  </div>
                ))}
                <button className="outline-btn full-btn" onClick={addCategory}>＋ Add category</button>
                {site.categories.length > 0 && bulkDeleteBox(["samples", "categories", "everything"])}
              </>
            )}

            {activeSection === "About us" && (
              <>
                <div className="field-section-title">About us page</div>
                <p className="field-help">Your store's About us page (linked in the footer and the phone menu). Leave an empty line between paragraphs. Empty text shows the short about description from Content.</p>
                <Field label="Page title" value={site.aboutPage.title} onChange={(v) => change("aboutPage", { ...site.aboutPage, title: v })} placeholder="About us" />
                <TextField label="Your story" value={site.aboutPage.text} onChange={(v) => change("aboutPage", { ...site.aboutPage, text: v })} rows={14} />
                <Field label="Picture URL (optional)" value={site.aboutPage.image} onChange={(v) => change("aboutPage", { ...site.aboutPage, image: v })} placeholder="https://example.com/shop.jpg" />
                <ImageUpload kind="banner" label="Or upload a picture" siteId={siteId} notify={notify} onUploaded={(url) => { change("aboutPage", { ...site.aboutPage, image: url }); refreshMedia(); }} />
                {site.aboutPage.image && <div className="image-url-preview"><img src={site.aboutPage.image} alt="About us" /><span>Picture preview</span></div>}
              </>
            )}

            {activeSection === "Blog" && (
              <>
                <div className="field-section-title">Blog</div>
                <p className="field-help">Posts are saved on their own (no need to press Save changes). Published posts show on your store's Blog page (linked in the header, footer and phone menu).</p>
                <BlogManager api={blogApi} siteUrl={meta.url} notify={notify} CoverUpload={BlogCover} />
              </>
            )}

            {activeSection === "Policies" && (
              <>
                <div className="field-section-title">Store legal pages</div>
                <p className="field-help">Edit the policy content displayed on your storefront. Have your final wording reviewed for your business and local requirements.</p>
                <div className="mode-switch policy-switch">
                  <button className={policyTab === "privacyPolicy" ? "active" : ""} onClick={() => setPolicyTab("privacyPolicy")}>Privacy policy</button>
                  <button className={policyTab === "termsConditions" ? "active" : ""} onClick={() => setPolicyTab("termsConditions")}>Terms & conditions</button>
                </div>
                <TextField label={policyTab === "privacyPolicy" ? "Privacy policy content" : "Terms & conditions content"} value={site[policyTab]} onChange={(v) => change(policyTab, v)} rows={18} className="policy-field" />
              </>
            )}

            {activeSection === "Design" && (
              <>
                <div className="field-section-title">Brand identity</div>
                <Field label="Store name" value={site.name} onChange={(v) => change("name", v)} />
                <label className="admin-field">Store type
                  <select value={site.template} onChange={(e) => change("template", e.target.value)}>
                    {Object.entries(STORE_TYPES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                  </select>
                </label>
                <p className="field-help">Sets the wording on your store, such as "Shop plants" or "Your shopping bag". Your products and banners stay as they are.</p>
                {/* The storefront's design: same content, a different look and order of the home page */}
                <div className="field-section-title">Website design</div>
                <div className="layout-picker" role="radiogroup" aria-label="Website design">
                  {[
                    ["", "Classic", "Full-photo banner, story circles for categories, elegant serif headings."],
                    ["v2", "Studio (v2)", "Split banner with a coloured panel, square category tiles, bold headings and boxed product cards."],
                  ].map(([value, name, text]) => (
                    <button type="button" role="radio" aria-checked={(site.layout || "") === value} key={name} className={(site.layout || "") === value ? "active" : ""} onClick={() => change("layout", value)}>
                      <span className={`layout-thumb ${value ? "is-v2" : ""}`} aria-hidden="true"><i /><i /><i /></span>
                      <b>{name}</b>
                      <small>{text}</small>
                    </button>
                  ))}
                </div>
                <div className="field-section-title">Logo</div>
                <div className="brand-slots">
                  {[
                    ["logo", "Store logo", "Shown before your store name. A square image looks best."],
                    ["favicon", "Browser-tab icon", "The small icon in browser tabs. A simple square image."],
                  ].map(([key, title, help]) => (
                    <div className="brand-slot" key={key}>
                      <b>{title}</b>
                      <small>{help}</small>
                      <div className="brand-slot-preview">{site[key] ? <img src={site[key]} alt={title} /> : <span className="field-help">No image yet</span>}</div>
                      <ImageUpload kind="logo" label="Upload" siteId={siteId} notify={notify} onUploaded={(url) => { change(key, url); refreshMedia(); }} />
                      {site[key] && <button className="outline-btn" onClick={() => change(key, "")}>Remove</button>}
                    </div>
                  ))}
                </div>
                <p className="field-help">Save changes to show them on your store. Without a logo, your store shows its name.</p>
                <div className="field-section-title">Theme colour</div>
                <div className="theme-picker">
                  <div className="theme-current">
                    <span className="theme-current-chip" style={{ background: themeColor }} />
                    <div><b>Your store colour</b><small>Buttons, links, badges, the announcement bar and the footer</small></div>
                  </div>
                  <div className="theme-swatches" role="radiogroup" aria-label="Theme colour">
                    {THEME_COLORS.map((c) => (
                      <button key={c} role="radio" aria-checked={c === themeColor} className={c === themeColor ? "active" : ""} style={{ background: c }} onClick={() => change("color", c)} aria-label={`Use ${c}`} />
                    ))}
                    {/* Any colour: the browser's own colour picker, behind a rainbow swatch */}
                    <label className={`theme-custom ${customColor ? "active" : ""}`} title="Pick any colour" style={customColor ? { background: themeColor } : undefined}>
                      <input type="color" value={themeColor} onChange={(e) => change("color", e.target.value)} aria-label="Pick any colour" />
                      <span aria-hidden="true">{customColor ? "✎" : "+"}</span>
                    </label>
                  </div>
                  <label className="admin-field theme-code">Colour code
                    <div className="theme-code-row"><span style={{ background: themeColor }} /><ColorCodeInput value={themeColor} onChange={(c) => change("color", c)} /></div>
                  </label>
                </div>
                <p className="field-help">Tap ＋ to pick any colour, or type a code such as #205f3d. Darker colours keep white button text easy to read. Save changes to apply it to your store.</p>
              </>
            )}

            {activeSection === "Shipping & tax" && (
              <>
                <p className="field-help">The delivery fee, free-shipping amount and GST rate for {site.name || "this website"} only. This section has its own Save button.</p>
                <StoreSettingsForm notify={notify} siteId={siteId} only={SHIPPING_CARD} />
              </>
            )}

            {activeSection === "Store settings" && (
              <>
                <div className="field-section-title">Store details</div>
                <Field label="Business category" value={site.category} onChange={(v) => change("category", v)} />
                <Field label="Custom domain" value={site.domain} disabled={!isSuper} onChange={(v) => change("domain", v)} placeholder={meta.subdomain} />
                <small className="field-help">{isSuper ? "Leave blank to use the free address " : "Only a Super Admin can connect a custom domain. Your store address is "}{meta.subdomain}.</small>
                <div className="field-section-title">Image storage</div>
                <StorageMeter media={effectiveMedia} />
                <div className="field-section-title">Emails</div>
                <EmailMeter emails={media.emails} />
                {user.role === "storeadmin" && (
                  <>
                    <p className="field-help">Shipping and GST are in the Shipping &amp; tax tab. Contact details and the payment gateway are in Dashboard → Profile.</p>
                    <button className="outline-btn full-btn" onClick={() => navigate("/admin/profile")}>Open store profile →</button>
                  </>
                )}
              </>
            )}
          </div>
          </div>
          <div className="editor-panel-footer">
            <span className={saved ? "is-saved" : "is-unsaved"}>{saved ? "✓ All changes are saved." : "● You have unsaved changes."}</span>
            <div className="editor-footer-actions">
              {previewButton}
              <button className="editor-save" onClick={save} disabled={saving || saved}>{saving ? <><Spinner /> Saving</> : "Save changes"}</button>
            </div>
          </div>
        </aside>

        {/* The real store, working as shoppers use it (menus, product pages, cart, checkout). It shows what is
            saved, and reloads after every save. */}
        <section className="preview-area">
          <div className="preview-toolbar">
            <div><span className="preview-status-dot" /> Your store <span className="preview-url">{previewDomain}</span></div>
            <div className="device-controls">
              <div className="mode-switch" role="radiogroup" aria-label="Preview size">
                <button role="radio" aria-checked={previewDevice === "desktop"} className={previewDevice === "desktop" ? "active" : ""} onClick={() => setPreviewDevice("desktop")}>🖥 Desktop</button>
                <button role="radio" aria-checked={previewDevice === "phone"} className={previewDevice === "phone" ? "active" : ""} onClick={() => setPreviewDevice("phone")}>📱 Phone</button>
              </div>
              <button onClick={() => setPreviewKey((k) => k + 1)}>↻ Reload</button>
              {meta.url && <a className="preview-open" href={meta.url} target="_blank" rel="noopener noreferrer">↗ Open in new tab</a>}
            </div>
          </div>
          {!saved && <div className="preview-unsaved">You have unsaved changes. Press <b>Save changes</b> to see them here.</div>}
          <div className={`store-frame ${previewDevice}`}>
            {meta.url ? <iframe key={previewKey} src={meta.url} title={`${site.name} store`} /> : <p className="field-help">The store's address is not known yet.</p>}
          </div>
        </section>
      </div>

      {productModal && (() => {
        const d = draftProduct;
        const set = (changes) => setDraftProduct((x) => ({ ...x, ...changes }));
        const isNew = typeof d.id === "string" && !site.products.some((p) => p.id === d.id);
        const optionName = d.optionLabel || (isGifts || isSelfcare ? "Option" : "Size");
        const colourCount = (d.colors || []).length;
        const close = () => !productSaving && setProductModal(false);
        return (
          <div className="product-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
            {/* One form for adding and editing. Head and buttons stay put; only the form between them scrolls */}
            <div className="product-modal add-product-modal simple-product-modal" role="dialog" aria-modal="true" aria-labelledby="add-product-title">
              <div className="product-modal-head">
                <div><h2 id="add-product-title">{isNew ? "Add product" : "Edit product"}</h2><p>Photo, name and price are all you need. Everything else is optional.</p></div>
                <button className="modal-close" onClick={close} aria-label="Close">×</button>
              </div>
              <div className="add-product-body">
                <section className="add-product-section">
                  <h3><span className="step-dot">1</span> Photos</h3>
                  <ProductPhotos
                    img={d.img}
                    onImgChange={(url) => set({ img: url })}
                    gallery={d.gallery || []}
                    onGalleryChange={(urls) => set({ gallery: urls })}
                    siteId={siteId}
                    notify={notify}
                    onUploaded={refreshMedia}
                    hasGallery={true}
                  />
                </section>
                <section className="add-product-section">
                  <h3><span className="step-dot">2</span> Name &amp; category</h3>
                  <div className="product-modal-grid">
                    <label className="admin-field">Product name *<input value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Monstera plant" autoFocus={isNew} /></label>
                    <label className="admin-field">Category *<select value={d.cat} onChange={(e) => set({ cat: e.target.value })}>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
                  </div>
                </section>
                <section className="add-product-section">
                  <h3><span className="step-dot">3</span> Price &amp; stock</h3>
                  <PriceFields p={d} optionName={optionName} onChange={set} presets={isSelfcare ? SELFCARE_PRESETS : isGifts ? GIFT_PRESETS : isClothing ? SIZE_PRESETS : GENERAL_PRESETS} optionPlaceholder={isSelfcare ? "e.g. Volume, Pack Size, Net Wt" : isGifts ? "e.g. Mug size, Frame size, Pack" : "Size (or Length, Width, Weight)"} />
                  {!(d.sizePrices || []).length && (
                    <p className="field-help add-product-discount">
                      {percentOff(d) > 0 ? <span className="offer-badge">{percentOff(d)}% off</span> : <span className="offer-badge none">No discount</span>}
                      {" "}The original price shows crossed out when it is higher.
                    </p>
                  )}
                  <div className="product-modal-grid">
                    <label className="admin-field">Stock quantity<input type="number" min="0" inputMode="numeric" value={d.stock} onChange={(e) => set({ stock: Math.max(0, Number(e.target.value)) })} /></label>
                    <label className="admin-field stock-switch">Available to buy
                      <span className="switch-row">
                        <input type="checkbox" checked={d.availability !== "Out of stock"} onChange={(e) => set({ availability: e.target.checked ? "In stock" : "Out of stock" })} />
                        <span>{d.availability === "Out of stock" ? "Out of stock" : "In stock"}</span>
                      </span>
                    </label>
                  </div>
                </section>
                <section className="add-product-section">
                  <h3><span className="step-dot">4</span> Description <em>optional</em></h3>
                  <label className="admin-field"><textarea rows="3" value={d.desc} onChange={(e) => set({ desc: e.target.value })} placeholder="What it is, size, material, what is included" aria-label="Description" /></label>
                </section>
                {/* Colours (sizes are set with the price): open for products that have some, folded away otherwise */}
                <details className="add-product-more" open={colourCount > 0}>
                  <summary>🎨 Colours <em>{colourCount ? `${colourCount} added` : "optional"}</em></summary>
                  <VariantFields colors={d.colors || []} photo={d.img} siteId={siteId} gifts={isGifts} selfcare={isSelfcare} onChange={set} />
                </details>
                <details className="add-product-more">
                  <summary>⚙️ More options <em>sold as, tags{isGifts ? ", personalisation" : ""}</em></summary>
                  <div className="product-modal-grid">
                    <label className="admin-field">Sold as<input value={d.unitNote} onChange={(e) => set({ unitNote: e.target.value })} placeholder="per pc, kg, litre, bundle" /></label>
                    <label className="admin-field">Tags<input value={(d.tags || []).join(", ")} onChange={(e) => set({ tags: parseTags(e.target.value) })} placeholder="Handmade, Gift ready" /></label>
                  </div>
                  {isGifts && <PersonalizationFields value={d.personalization} onChange={(value) => set({ personalization: value })} />}
                </details>
              </div>
              <div className="product-modal-actions">
                {!isNew && <button className="danger-text-btn product-modal-delete" disabled={productSaving} onClick={async () => { if (await removeProduct(d.id)) setProductModal(false); }}>🗑️ Delete</button>}
                <button className="outline-btn" onClick={close} disabled={productSaving}>Cancel</button>
                <button className="primary-btn" onClick={saveProduct} disabled={productSaving}>{productSaving ? "Saving…" : isNew ? "＋ Add product" : "✓ Save product"}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
